import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import { Trash2 } from 'lucide-react';

interface NotesTabProps {
  asset: UnifiedAsset;
}

export function NotesTab({ asset }: NotesTabProps) {
  const assetId = useMemo(() => String((asset as any)?.id), [asset]);
  const [noteText, setNoteText] = useState('');
  const [uid, setUid] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUid(data?.session?.user?.id ?? null)).catch(() => setUid(null));
  }, []);

  const { data: notes, refetch, isLoading } = useQuery({
    queryKey: ['asset-notes', assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asset_notes')
        .select('*')
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const onAdd = async () => {
    const content = noteText.trim();
    if (!uid) { toast.error('You must be signed in to add notes.'); return; }
    if (!content) { toast.error('Please enter a note.'); return; }
    const { data, error } = await supabase
      .from('asset_notes')
      .insert({ asset_id: assetId, user_id: uid, content })
      .select()
      .single();
    if (error) { toast.error('Failed to add note.'); return; }
    toast.success('Note added.');
    setNoteText('');
    await refetch();
  };

  const onDelete = async (id: string) => {
    const { error } = await supabase
      .from('asset_notes')
      .delete()
      .eq('id', id);
    if (error) { toast.error('Failed to delete note.'); return; }
    toast.success('Note deleted.');
    await refetch();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Create Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <label htmlFor="assetNoteContent" className="sr-only">Add a note</label>
            <Textarea
              id="assetNoteContent"
              name="assetNoteContent"
              rows={3}
              placeholder="Type a note and click Add"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={onAdd}>Add Note</Button>
            </div>
          </div>

          <div className="space-y-2">
            {(notes || []).length === 0 && !isLoading && (
              <div className="text-sm text-muted-foreground">No notes yet.</div>
            )}
            {(notes || []).map((n: any) => (
              <div key={n.id} className="flex items-start justify-between p-3 bg-muted rounded-lg">
                <div className="pr-3">
                  <div className="whitespace-pre-wrap text-sm">{n.content}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDelete(n.id)} aria-label="Delete note">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
